import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Row,
  Col,
  DatePicker,
  Button,
  Space,
  Typography,
  message,
  Select,
  Card,
  Tooltip,
  Modal,
  Spin,
  Divider,
  Input,
  Checkbox,
  Table
} from 'antd';
import {
  CalendarOutlined,SwapOutlined,
  ReloadOutlined,
  SearchOutlined,
  HomeOutlined,
  UserOutlined,
  TeamOutlined,
  FileTextOutlined,
  LeftOutlined,
  RightOutlined,
  FilterOutlined,
  ClearOutlined,
  EyeOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import 'dayjs/locale/es';

import PageContainer from '../components/PageContainer';
import CambioHabitacion from '../components/CambioHabitacion';
import { BASE_URL } from '../utils/constants';

dayjs.extend(isBetween);
dayjs.locale('es');

const { RangePicker } = DatePicker;
const { Option } = Select;

const PlaningHabitaciones = () => {
  // Estados principales
  const [loading, setLoading] = useState(false);
  const [fechas, setFechas] = useState({
    desde: dayjs().startOf('month'),
    hasta: dayjs().endOf('month')
  });
  const [fechasRango, setFechasRango] = useState([dayjs().startOf('month'), dayjs().endOf('month')]);
  const [habitaciones, setHabitaciones] = useState([]);
  const [fechasList, setFechasList] = useState([]);
  const [tiposHabitacion, setTiposHabitacion] = useState([]);
  
  // Filtros
  const [filtros, setFiltros] = useState({
    tipoHabitacion: '',
    piso: '',
    buscar: '',
    soloOcupadas: false,
    soloBloqueadas: false
  });
  
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [anchuraCelda, setAnchuraCelda] = useState(60);
  
  // Modal reserva
  const [modalReservaVisible, setModalReservaVisible] = useState(false);
  const [reservaSeleccionada, setReservaSeleccionada] = useState(null);
  const [loadingReserva, setLoadingReserva] = useState(false);
  
  // Hover
  const [hoverReserva, setHoverReserva] = useState(null);

  const [cambioModalVisible, setCambioModalVisible] = useState(false);
  const [reservaParaCambio, setReservaParaCambio] = useState(null);  
  
  const containerRef = useRef(null);

  // Colores para reservas
  const coloresReserva = {
    L: { bg: '#e6f7ff', border: '#91d5ff', text: '#0050b3' },
    B: { bg: '#fff1f0', border: '#ffa39e', text: '#a8071a' },
    O: { bg: '#f6ffed', border: '#b7eb8f', text: '#237804' },
    R: { bg: '#fff7e6', border: '#ffd591', text: '#ad4e00' },
    D: { bg: '#f9f0ff', border: '#d3adf7', text: '#391085' },
    default: { bg: '#fafafa', border: '#f0f0f0', text: '#000000' }
  };

  // Cargar tipos de habitación al inicio
  useEffect(() => {
    cargarTiposHabitacion();
  }, []);

  // Cargar planing cuando cambian las fechas
  useEffect(() => {
    if (fechas.desde && fechas.hasta) {
      cargarPlaning();
    }
  }, [fechas.desde, fechas.hasta]);

  const cargarTiposHabitacion = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/planing/tipos-habitacion`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTiposHabitacion(response.data.data || []);
    } catch (error) {
      console.error('Error cargando tipos:', error);
    }
  };

  const cargarPlaning = async () => {
    if (!fechas.desde || !fechas.hasta) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const params = {
        fechaDesde: fechas.desde.format('YYYY-MM-DD'),
        fechaHasta: fechas.hasta.format('YYYY-MM-DD'),
        tipoHabitacion: filtros.tipoHabitacion
      };
      
      const response = await axios.get(`${BASE_URL}/api/planing`, {
        headers: { Authorization: `Bearer ${token}` },
        params: params
      });

      if (response.data.success) {
        const data = response.data.data;
        setHabitaciones(data.habitaciones || []);
        setFechasList(data.fechas || []);
        
        // Ajustar anchura de celda
        const numDias = data.fechas?.length || 30;
        if (numDias > 45) setAnchuraCelda(45);
        else if (numDias > 31) setAnchuraCelda(50);
        else setAnchuraCelda(60);
      }
    } catch (error) {
      console.error('Error cargando planing:', error);
      message.error('Error al cargar el planing');
    } finally {
      setLoading(false);
    }
  };

  const handleRangoChange = (dates) => {
    if (dates && dates.length === 2) {
      setFechasRango(dates);
      setFechas({
        desde: dates[0],
        hasta: dates[1]
      });
    }
  };

  const handleNavegarSemana = (direccion) => {
    const nuevaFechaDesde = direccion === 'adelante' 
      ? fechas.desde.add(7, 'day')
      : fechas.desde.subtract(7, 'day');
    
    const nuevaFechaHasta = direccion === 'adelante'
      ? fechas.hasta.add(7, 'day')
      : fechas.hasta.subtract(7, 'day');
    
    setFechas({
      desde: nuevaFechaDesde,
      hasta: nuevaFechaHasta
    });
    setFechasRango([nuevaFechaDesde, nuevaFechaHasta]);
  };

  const handleLimpiarFiltros = () => {
    setFiltros({
      tipoHabitacion: '',
      piso: '',
      buscar: '',
      soloOcupadas: false,
      soloBloqueadas: false
    });
    // Recargar sin filtros
    setTimeout(() => cargarPlaning(), 0);
  };

  const handleVerReserva = async (numReserva) => {
    if (!numReserva || numReserva === 0) return;
    
    setLoadingReserva(true);
    setModalReservaVisible(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/planing/reserva/${numReserva}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setReservaSeleccionada(response.data.data);
      }
    } catch (error) {
      console.error('Error cargando reserva:', error);
      message.error('Error al cargar los detalles');
      setModalReservaVisible(false);
    } finally {
      setLoadingReserva(false);
    }
  };

  const handleAbrirCambioHabitacion = (reserva) => {
    setReservaParaCambio({
      NumReserva: reserva.NumReserva,
      NumLinea: reserva.NumLinea || 1,
      Nombre: reserva.Nombre,
      NumHabitacion: reserva.Habitaciones?.[0]?.NumHabitacio || reserva.NumHabitacion,
      Desde: reserva.FechaEntrada || reserva.Desde,
      Hasta: reserva.FechaSalida || reserva.Hasta,
      Pax: reserva.Pax,
      Observacion: ''
    });
    setCambioModalVisible(true);
  };  

  // Función para obtener info de celda
  const getInfoCelda = (habitacion, fecha) => {
    const fechaObj = dayjs(fecha).startOf('day');
    
    const reserva = habitacion.Reservas?.find(res => {
      const desde = dayjs(res.Desde).startOf('day');
      const hasta = dayjs(res.Hasta).startOf('day');
      return fechaObj.isBetween(desde, hasta, 'day', '[)');
    });
    
    if (reserva) {
      const esEntrada = fechaObj.isSame(dayjs(reserva.Desde).startOf('day'), 'day');
      const esSalida = fechaObj.isSame(dayjs(reserva.Hasta).startOf('day'), 'day');
      const esPrimeraCelda = fechaObj.isSame(dayjs(reserva.Desde).startOf('day'), 'day');
      
      const color = coloresReserva[reserva.Tipo] || coloresReserva.default;
      
      return {
        tieneReserva: true,
        reserva,
        color: color.bg,
        borderColor: color.border,
        textColor: color.text,
        esEntrada,
        esSalida,
        esPrimeraCelda,
        texto: reserva.Nombre ? 
          (reserva.Nombre.length > 8 ? reserva.Nombre.substring(0, 8) + '…' : reserva.Nombre) 
          : 'S/N',
        pax: reserva.Pax || 1
      };
    }
    
    return {
      tieneReserva: false,
      color: '#ffffff',
      borderColor: '#f0f0f0',
      texto: ''
    };
  };

  // Filtrar habitaciones
  const habitacionesFiltradas = useMemo(() => {
    if (!habitaciones.length) return [];
    
    return habitaciones.filter(hab => {
      if (filtros.tipoHabitacion && hab.TipoHabitacion !== filtros.tipoHabitacion) return false;
      if (filtros.piso && hab.Piso !== parseInt(filtros.piso)) return false;
      
      if (filtros.buscar && filtros.buscar.trim() !== '') {
        const busqueda = filtros.buscar.toLowerCase().trim();
        const coincideNumero = hab.NumHabitacion?.toString().includes(busqueda);
        const coincideReserva = hab.Reservas?.some(r => 
          r.Nombre?.toLowerCase().includes(busqueda) ||
          r.NumReserva?.toString().includes(busqueda)
        );
        if (!coincideNumero && !coincideReserva) return false;
      }
      
      if (filtros.soloOcupadas || filtros.soloBloqueadas) {
        const hoy = dayjs().startOf('day');
        const tieneReservaHoy = hab.Reservas?.some(res => {
          const desde = dayjs(res.Desde).startOf('day');
          const hasta = dayjs(res.Hasta).startOf('day');
          return hoy.isBetween(desde, hasta, 'day', '[)');
        });
        
        if (filtros.soloOcupadas) {
          const esOcupada = tieneReservaHoy && !hab.Reservas?.some(r => {
            const desde = dayjs(r.Desde).startOf('day');
            const hasta = dayjs(r.Hasta).startOf('day');
            return hoy.isBetween(desde, hasta, 'day', '[)') && r.Tipo === 'B';
          });
          if (!esOcupada) return false;
        }
        
        if (filtros.soloBloqueadas) {
          const esBloqueada = hab.Reservas?.some(r => {
            const desde = dayjs(r.Desde).startOf('day');
            const hasta = dayjs(r.Hasta).startOf('day');
            return hoy.isBetween(desde, hasta, 'day', '[)') && r.Tipo === 'B';
          });
          if (!esBloqueada) return false;
        }
      }
      
      return true;
    });
  }, [habitaciones, filtros]);

  // Agrupar por piso
  const habitacionesPorPiso = useMemo(() => {
    const grupos = {};
    habitacionesFiltradas.forEach(hab => {
      const piso = hab.Piso || 0;
      if (!grupos[piso]) grupos[piso] = [];
      grupos[piso].push(hab);
    });
    
    Object.keys(grupos).forEach(piso => {
      grupos[piso].sort((a, b) => a.NumHabitacion - b.NumHabitacion);
    });
    
    return grupos;
  }, [habitacionesFiltradas]);

  const pisos = Object.keys(habitacionesPorPiso).sort((a, b) => parseInt(a) - parseInt(b));

  return (
    <PageContainer
      title="Planing de Habitaciones"
      subtitle={`${fechasList.length} días • ${habitacionesFiltradas.length} habitaciones`}
      icon={<CalendarOutlined />}
      loading={loading}
      onRefresh={cargarPlaning}
      extra={
        <Space size={2}>
          <Button 
            type={mostrarFiltros ? 'primary' : 'default'}
            icon={<FilterOutlined />} 
            onClick={() => setMostrarFiltros(!mostrarFiltros)}
            size="small"
          />
          <Button 
            icon={<ReloadOutlined />} 
            onClick={cargarPlaning}
            size="small"
          />
        </Space>
      }
    >
      {/* Barra de herramientas */}
      <div style={{ marginBottom: 8, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <RangePicker
          value={fechasRango}
          onChange={handleRangoChange}
          format="DD/MM/YY"
          allowClear={false}
          size="small"
          style={{ width: 220 }}
        />
        
        <Button 
          type="primary" 
          icon={<SearchOutlined />} 
          onClick={cargarPlaning}
          loading={loading}
          size="small"
        >
          Consultar
        </Button>
        
        <Button 
          icon={<LeftOutlined />} 
          onClick={() => handleNavegarSemana('atras')}
          size="small"
        />
        <Button 
          icon={<RightOutlined />} 
          onClick={() => handleNavegarSemana('adelante')}
          size="small"
        />
      </div>

      {/* Filtros */}
      {mostrarFiltros && (
        <Card size="small" style={{ marginBottom: 8 }}>
          <Space wrap size={[8, 8]}>
            <Select
              placeholder="Tipo habitación"
              value={filtros.tipoHabitacion || undefined}
              onChange={(v) => setFiltros({...filtros, tipoHabitacion: v})}
              style={{ width: 130 }}
              size="small"
              allowClear
            >
              {tiposHabitacion.map(t => <Option key={t} value={t}>{t}</Option>)}
            </Select>
            
            <Select
              placeholder="Piso"
              value={filtros.piso || undefined}
              onChange={(v) => setFiltros({...filtros, piso: v})}
              style={{ width: 100 }}
              size="small"
              allowClear
            >
              {pisos.map(p => <Option key={p} value={p}>Piso {p}</Option>)}
            </Select>
            
            <Input
              placeholder="Buscar habitación/cliente..."
              value={filtros.buscar}
              onChange={(e) => setFiltros({...filtros, buscar: e.target.value})}
              style={{ width: 200 }}
              size="small"
              allowClear
              prefix={<SearchOutlined />}
            />
            
            <Checkbox 
              checked={filtros.soloOcupadas}
              onChange={(e) => setFiltros({...filtros, soloOcupadas: e.target.checked, soloBloqueadas: false})}
            >
              Solo ocupadas
            </Checkbox>
            
            <Checkbox 
              checked={filtros.soloBloqueadas}
              onChange={(e) => setFiltros({...filtros, soloBloqueadas: e.target.checked, soloOcupadas: false})}
            >
              Solo bloqueadas
            </Checkbox>
            
            <Button 
              icon={<ClearOutlined />} 
              onClick={handleLimpiarFiltros}
              size="small"
            >
              Limpiar
            </Button>
          </Space>
        </Card>
      )}

      {/* Contenedor del planing */}
      <div 
        ref={containerRef}
        style={{ 
          overflow: 'auto',
          maxHeight: 'calc(100vh - 220px)',
          border: '1px solid #f0f0f0',
          borderRadius: 4,
          backgroundColor: 'white'
        }}
      >
        {habitacionesFiltradas.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>
            No hay habitaciones para mostrar
          </div>
        ) : (
          <div style={{ minWidth: fechasList.length * anchuraCelda + 140 }}>
            {/* Cabecera de fechas */}
            <div style={{ display: 'flex', borderBottom: '1px solid #d9d9d9', backgroundColor: '#fafafa', position: 'sticky', top: 0, zIndex: 2 }}>
              <div style={{ 
                width: 140, 
                padding: '8px',
                fontWeight: 'bold',
                borderRight: '1px solid #d9d9d9',
                backgroundColor: '#f0f0f0',
                position: 'sticky',
                left: 0,
                zIndex: 3
              }}>
                Habitación
              </div>
              {fechasList.map((fecha, index) => {
                const fechaObj = dayjs(fecha);
                const esHoy = fechaObj.isSame(dayjs(), 'day');
                
                return (
                  <div 
                    key={index}
                    style={{ 
                      width: anchuraCelda,
                      padding: '4px 2px',
                      textAlign: 'center',
                      borderRight: '1px solid #d9d9d9',
                      backgroundColor: esHoy ? '#e6f7ff' : '#fafafa',
                      fontWeight: esHoy ? 'bold' : 'normal',
                      fontSize: 11
                    }}
                  >
                    <div>{fechaObj.format('DD')}</div>
                    <div style={{ color: '#999', fontSize: 10 }}>{fechaObj.format('ddd')[0]}</div>
                  </div>
                );
              })}
            </div>

            {/* Filas de habitaciones */}
            {pisos.map(piso => (
              <React.Fragment key={piso}>
                {/* Cabecera de piso */}
                <div style={{ 
                  display: 'flex', 
                  backgroundColor: '#e6f7ff',
                  borderBottom: '1px solid #91d5ff',
                  fontWeight: 'bold',
                  position: 'sticky',
                  top: 37,
                  zIndex: 2
                }}>
                  <div style={{ 
                    width: 140, 
                    padding: '4px 8px',
                    borderRight: '1px solid #91d5ff',
                    position: 'sticky',
                    left: 0,
                    backgroundColor: '#bae7ff',
                    zIndex: 3
                  }}>
                    PISO {piso === '0' ? 'BAJA' : piso}
                  </div>
                  <div style={{ width: fechasList.length * anchuraCelda }} />
                </div>

                {/* Habitaciones del piso */}
                {habitacionesPorPiso[piso].map((habitacion, idx) => (
                  <div 
                    key={habitacion.NumHabitacion}
                    style={{ 
                      display: 'flex',
                      borderBottom: idx < habitacionesPorPiso[piso].length - 1 ? '1px dashed #d9d9d9' : 'none',
                      backgroundColor: idx % 2 === 0 ? '#ffffff' : '#fafafa'
                    }}
                  >
                    {/* Columna habitación */}
                    <div style={{ 
                      width: 140, 
                      padding: '4px 8px',
                      borderRight: '1px solid #d9d9d9',
                      backgroundColor: '#f5f5f5',
                      position: 'sticky',
                      left: 0,
                      zIndex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4
                    }}>
                      <span style={{ 
                        backgroundColor: '#1890ff', 
                        color: 'white', 
                        padding: '2px 6px', 
                        borderRadius: 4,
                        fontWeight: 'bold',
                        minWidth: 35,
                        textAlign: 'center',
                        fontSize: 12
                      }}>
                        {habitacion.NumHabitacion}
                      </span>
                      <span style={{ 
                        backgroundColor: '#722ed1', 
                        color: 'white', 
                        padding: '2px 4px', 
                        borderRadius: 4,
                        fontSize: 10
                      }}>
                        {habitacion.HabitacionVenta2 || 'N/A'} 
                      </span>
                    </div>

                    {/* Celdas de días */}
                    {fechasList.map((fecha, idxFecha) => {
                      const info = getInfoCelda(habitacion, fecha);
                      
                      return (
                        <Tooltip 
                          key={idxFecha} 
                          title={info.tieneReserva ? (
                            <div style={{ padding: 4 }}>
                              <div><strong>{info.reserva.Nombre}</strong></div>
                              <div>Reserva: {info.reserva.NumReserva}</div>
                              <div>Entrada: {dayjs(info.reserva.Desde).format('DD/MM/YY')}</div>
                              <div>Salida: {dayjs(info.reserva.Hasta).format('DD/MM/YY')}</div>
                              <div>Pax: {info.reserva.Pax}</div>
                              <div>Estado: {info.reserva.Confirmada ? '✓' : '⏳'}</div>
                              <Divider style={{ margin: '8px 0' }} />
                                    <Button 
                                      type="primary" 
                                      size="small" 
                                      icon={<SwapOutlined />}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleAbrirCambioHabitacion({
                                          NumReserva: info.reserva.NumReserva,
                                          NumLinea: info.reserva.NumLinea || 1,
                                          Nombre: info.reserva.Nombre,
                                          NumHabitacion: habitacion.NumHabitacion,
                                          Desde: info.reserva.Desde,
                                          Hasta: info.reserva.Hasta,
                                          Pax: info.reserva.Pax,
                                          Observacion: ''
                                        });
                                      }}
                                      style={{ width: '100%' }}
                                    >
                                      Cambiar habitación
                                    </Button>                              
                            </div>
                          ) : 'Libre'}
                          placement="top"
                        >
                          <div
                            onClick={() => info.tieneReserva && handleVerReserva(info.reserva.NumReserva)}
                            onMouseEnter={() => info.tieneReserva && setHoverReserva(info.reserva.NumReserva)}
                            onMouseLeave={() => setHoverReserva(null)}
                            style={{ 
                              width: anchuraCelda,
                              padding: 2,
                              backgroundColor: info.color,
                              borderLeft: info.esEntrada ? '3px solid #52c41a' : 'none',
                              borderRight: info.esSalida ? '3px solid #ff4d4f' : '1px solid #d9d9d9',
                              cursor: info.tieneReserva ? 'pointer' : 'default',
                              fontSize: 10,
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                              position: 'relative',
                              boxShadow: info.tieneReserva && hoverReserva === info.reserva?.NumReserva
                                ? 'inset 0 0 0 2px #1677ff'
                                : 'none',
                              transition: 'all 0.2s'
                            }}
                          >
                            {info.tieneReserva && (
                              <>
                                {info.esPrimeraCelda ? (
                                  <>
                                    <div style={{ fontWeight: 'bold', color: info.textColor }}>
                                      {info.texto}
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: '#666' }}>
                                      <span>{info.reserva.NumReserva}</span>
                                      <span>{info.pax}p</span>
                                    </div>
                                  </>
                                ) : (
                                  <div style={{ 
                                    height: '100%', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    color: info.textColor,
                                    opacity: 0.3,
                                    fontSize: 8
                                  }}>
                                    ●
                                  </div>
                                )}
                                
                                {info.esEntrada && (
                                  <span style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 2,
                                    fontSize: 10,
                                    color: '#52c41a'
                                  }}>
                                    📥
                                  </span>
                                )}
                                
                                {info.esSalida && (
                                  <span style={{
                                    position: 'absolute',
                                    top: 0,
                                    right: 2,
                                    fontSize: 10,
                                    color: '#ff4d4f'
                                  }}>
                                    🚪
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                        </Tooltip>
                      );
                    })}
                  </div>
                ))}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      {/* Modal de reserva */}
      <Modal
        title={
          <Space>
            <FileTextOutlined />
            <span>Reserva #{reservaSeleccionada?.NumReserva}</span>
            {reservaSeleccionada?.Confirmada ? (
              <span style={{ color: '#52c41a' }}>✓ Confirmada</span>
            ) : (
              <span style={{ color: '#fa8c16' }}>⏳ Pendiente</span>
            )}
          </Space>
        }
        open={modalReservaVisible}
        onCancel={() => setModalReservaVisible(false)}
        footer={null}
        width={700}
      >
        {loadingReserva ? (
          <div style={{ textAlign: 'center', padding: 30 }}>
            <Spin />
          </div>
        ) : reservaSeleccionada && (
          <>
            <div style={{ 
              backgroundColor: '#f5f5f5', 
              padding: 12, 
              borderRadius: 4,
              marginBottom: 16,
              display: 'flex',
              gap: 20
            }}>
              <span><UserOutlined /> {reservaSeleccionada.Nombre}</span>
              <span><TeamOutlined /> {reservaSeleccionada.Pax || 1} pax</span>
              <span><HomeOutlined /> {reservaSeleccionada.Habitaciones?.length || 1} hab</span>
            </div>

            {/* Botón de cambio de habitación */}
            <Button
              type="primary"
              icon={<SwapOutlined />}
              onClick={() => {
                setModalReservaVisible(false);
                handleAbrirCambioHabitacion(reservaSeleccionada);
              }}
              size="small"
            >
              Cambiar habitación
            </Button>            

            <Row gutter={[16, 16]}>
              <Col span={24}>
                <table style={{ width: '100%' }}>
                  <tbody>
                    <tr>
                      <td style={{ padding: 8, backgroundColor: '#fafafa', width: 120 }}>Agencia</td>
                      <td style={{ padding: 8 }}>{reservaSeleccionada.Agencia || 'Directa'}</td>
                      <td style={{ padding: 8, backgroundColor: '#fafafa', width: 120 }}>Entrada</td>
                      <td style={{ padding: 8 }}>{dayjs(reservaSeleccionada.FechaEntrada).format('DD/MM/YYYY')}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: 8, backgroundColor: '#fafafa' }}>Salida</td>
                      <td style={{ padding: 8 }}>{dayjs(reservaSeleccionada.FechaSalida).format('DD/MM/YYYY')}</td>
                      <td style={{ padding: 8, backgroundColor: '#fafafa' }}>Noches</td>
                      <td style={{ padding: 8 }}>{dayjs(reservaSeleccionada.FechaSalida).diff(dayjs(reservaSeleccionada.FechaEntrada), 'day')}</td>
                    </tr>
                  </tbody>
                </table>
              </Col>

              {reservaSeleccionada.Habitaciones?.length > 0 && (
                <Col span={24}>
                  <Divider style={{ margin: '12px 0' }}>Habitaciones</Divider>
                  <Table
                    dataSource={reservaSeleccionada.Habitaciones}
                    rowKey={(record, idx) => `${record.NumHabitacio}-${idx}`}
                    pagination={false}
                    size="small"
                    columns={[
                      { title: 'Habitación', dataIndex: 'NumHabitacio', width: 100 },
                      { title: 'Tipo', dataIndex: 'TipoHabitacion', width: 100 },
                      { title: 'Precio', dataIndex: 'Precio', width: 100, render: p => `$${p?.toFixed(2) || '0.00'}` },
                      { title: 'Pax', dataIndex: 'Pax', width: 80 }
                    ]}
                  />
                </Col>
              )}
            </Row>
          </>
        )}
      </Modal>


      <CambioHabitacion
        visible={cambioModalVisible}
        onCancel={() => {
          setCambioModalVisible(false);
          setReservaParaCambio(null);
        }}
        onSuccess={(data) => {
          setCambioModalVisible(false);
          setReservaParaCambio(null);
          // Recargar el planing para reflejar el cambio
          cargarPlaning();
          message.success(`Habitación cambiada exitosamente a ${data.habitacionDestino}`);
        }}
        reservaData={reservaParaCambio}
        desdePlanning={true}
      />

    </PageContainer>
  );
};

export default PlaningHabitaciones;